import React, { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useAppData } from '../../context/AppContext'
import { dataService } from '../../services/dataService'
import { Customer } from '../../types'
import { Modal } from '../ui/Modal'
import { Plus, EditIcon, Trash2 } from '../icons/Icons'
import { useVersionControl } from '../../hooks/useVersionControl'
import { ConflictModal } from '../ui/ConflictModal'
import { BulkUploadModal } from '../shared/BulkUploadModal'
import s from './shared.module.css'

const INDUSTRIES = ['Technology', 'Manufacturing', 'Financial Services', 'Healthcare', 'Retail', 'Government', 'Other']
const REGIONS = ['North America', 'Europe', 'Asia Pacific', 'Latin America', 'Middle East & Africa']

export default function Customers() {
  const { organization, user, orgMember } = useAuth()
  const { customers, setCustomers, allCustomers, resetCustomers, refreshData } = useAppData()
  const orgId = organization!.id

  const canBulkUpload = orgMember?.user_type === 'owner' || orgMember?.user_type === 'admin' || orgMember?.user_type === 'manager'

  const [showModal, setShowModal] = useState(false)
  const [showBulkUpload, setShowBulkUpload] = useState(false)
  const [editing, setEditing] = useState<Customer | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [filters, setFilters] = useState({ name: '', industry: '', region: '' })
  const [form, setForm] = useState({ title: '', account_manager: '', industry: '', region: '' })

  const {
    saveWithVersionCheck,
    activeEditors,
    conflict,
    clearConflict
  } = useVersionControl<Customer & { version: number }>({
    tableName: 'customers',
    recordId: editing?.id || null
  })

  const openAdd = () => { setEditing(null); setForm({ title: '', account_manager: '', industry: '', region: '' }); setSaveError(''); setShowModal(true) }
  const openEdit = (c: Customer) => { setEditing(c); setForm({ title: c.title, account_manager: c.account_manager, industry: c.industry, region: c.region }); setSaveError(''); setShowModal(true) }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (saving) return
    setSaving(true); setSaveError('')
    try {
      if (editing) {
        const result = await saveWithVersionCheck(
          editing as Customer & { version: number },
          form,
          `Updated customer: ${form.title}`
        )

        if (result.conflict) {
          setSaveError('Version conflict detected. Please review and resolve.')
          return
        }

        if (!result.success) {
          setSaveError('Failed to save. Please try again.')
          return
        }
      } else {
        await dataService.createCustomer(orgId, form as any)
      }
      setShowModal(false)
      await refreshData()
    } catch (err: any) { setSaveError(err?.message || 'Failed to save. Please try again.') }
    finally { setSaving(false) }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this customer?')) return
    await dataService.deleteCustomer(id)
    await refreshData()
  }

  const applyFilters = () => {
    let result = allCustomers
    if (filters.name) result = result.filter(c => c.title.toLowerCase().includes(filters.name.toLowerCase()))
    if (filters.industry) result = result.filter(c => c.industry === filters.industry)
    if (filters.region) result = result.filter(c => c.region === filters.region)
    setCustomers(result)
  }

  const resetFilters = () => { setFilters({ name: '', industry: '', region: '' }); resetCustomers() }

  return (
    <div>
      <div className={s.pageHeader}>
        <div>
          <h2 className={s.pageTitle}>Customers</h2>
          <p className={s.pageSubtitle}>{allCustomers.length} customer{allCustomers.length !== 1 ? 's' : ''}</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {canBulkUpload && <button className={s.secondaryBtn} onClick={() => setShowBulkUpload(true)}>Bulk Upload</button>}
          <button className={s.primaryBtn} onClick={openAdd}><Plus size={14} /> Add Customer</button>
        </div>
      </div>

      <div className={s.card}>
        <div className={s.filterBar}>
          <input className={s.filterInput} placeholder="Search by name..." value={filters.name}
            onChange={e => setFilters(f => ({ ...f, name: e.target.value }))} />
          <select className={s.filterSelect} value={filters.industry} onChange={e => setFilters(f => ({ ...f, industry: e.target.value }))}>
            <option value="">All Industries</option>
            {INDUSTRIES.map(i => <option key={i}>{i}</option>)}
          </select>
          <select className={s.filterSelect} value={filters.region} onChange={e => setFilters(f => ({ ...f, region: e.target.value }))}>
            <option value="">All Regions</option>
            {REGIONS.map(r => <option key={r}>{r}</option>)}
          </select>
          <button className={s.primaryBtn} onClick={applyFilters}>Filter</button>
          <button className={s.secondaryBtn} onClick={resetFilters}>Reset</button>
        </div>

        <table className={s.table}>
          <thead>
            <tr>
              <th>Name</th><th>Account Manager</th><th>Industry</th><th>Region</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {customers.length === 0 && (
              <tr><td colSpan={5} className={s.emptyState}>No customers found</td></tr>
            )}
            {customers.map(c => (
              <tr key={c.id}>
                <td style={{ fontWeight: 600 }}>{c.title}</td>
                <td>{c.account_manager || '—'}</td>
                <td>{c.industry || '—'}</td>
                <td>{c.region || '—'}</td>
                <td>
                  <div className={s.actions}>
                    <button className={`${s.actionBtn} ${s.edit}`} onClick={() => openEdit(c)} title="Edit"><EditIcon size={14} /></button>
                    <button className={`${s.actionBtn} ${s.del}`} onClick={() => handleDelete(c.id)} title="Delete"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showBulkUpload && (
        <BulkUploadModal
          entityType="customers"
          orgId={orgId}
          userId={user!.id}
          onClose={() => setShowBulkUpload(false)}
          onSuccess={() => { setShowBulkUpload(false); refreshData() }}
        />
      )}

      <Modal open={showModal} title={editing ? 'Edit Customer' : 'Add Customer'} onClose={() => setShowModal(false)}
        footer={
          <>
            <button className={s.secondaryBtn} onClick={() => setShowModal(false)}>Cancel</button>
            <button className={s.primaryBtn} form="customerForm" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
          </>
        }>
        <form id="customerForm" onSubmit={handleSubmit}>
          {saveError && <div className={s.formError}>{saveError}</div>}
          {editing && activeEditors.length > 0 && (
            <div style={{ padding: '12px', marginBottom: '16px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', fontSize: '14px' }}>
              <strong>⚠️ Currently being edited by:</strong> {activeEditors.filter(e => e.user_id !== user?.id).map(e => e.display_name).join(', ') || 'another user'}
            </div>
          )}
          <div className={s.formGroup}>
            <label>Company Name *</label>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required />
          </div>
          <div className={s.formGroup}>
            <label>Account Manager</label>
            <input value={form.account_manager} onChange={e => setForm(f => ({ ...f, account_manager: e.target.value }))} />
          </div>
          <div className={s.formRow}>
            <div className={s.formGroup}>
              <label>Industry</label>
              <select value={form.industry} onChange={e => setForm(f => ({ ...f, industry: e.target.value }))}>
                <option value="">Select...</option>
                {INDUSTRIES.map(i => <option key={i}>{i}</option>)}
              </select>
            </div>
            <div className={s.formGroup}>
              <label>Region</label>
              <select value={form.region} onChange={e => setForm(f => ({ ...f, region: e.target.value }))}>
                <option value="">Select...</option>
                {REGIONS.map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
          </div>
        </form>
      </Modal>

      {conflict && (
        <ConflictModal
          conflict={conflict}
          onReload={async () => {
            clearConflict()
            await refreshData()
            setSaving(false)
          }}
          onOverride={async () => {
            clearConflict()
            setSaving(false)
          }}
          onCancel={() => {
            clearConflict()
            setSaving(false)
          }}
        />
      )}
    </div>
  )
}
